import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  PhotoIcon,
  VideoCameraIcon,
  ComputerDesktopIcon,
  UserIcon,
  SpeakerWaveIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { getPublicQuestionnaire, startQuestionnaire, submitQuestionAnswer, uploadVoiceAnswer } from '../../Requests/QuestionnaireRequests';
import UpgradedLoader from '../../Components/UpgradedLoader';
import useQuestionnaireRecording from '../../hooks/useQuestionnaireRecording';
import { captureAois } from '../../utils/aoiCapture';
import GazeCalibration from '../../Components/Questionnaire/GazeCalibration';
import VoiceAnswerRecorder from '../../Components/Questionnaire/VoiceAnswerRecorder';

const QuestionnaireViewer = () => {
  const { questionnaireUuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const pendingNextRef = useRef(null);
  const advancingRef = useRef(false); // guards the pre-save frame-capture window
  const [viewStartTime, setViewStartTime] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);
  const [responseUuid, setResponseUuid] = useState(null);
  const [isStarting, setIsStarting] = useState(false);

  const recording = useQuestionnaireRecording();
  const [wantScreen, setWantScreen] = useState(false);
  const [wantCamera, setWantCamera] = useState(false);
  const [wantMouse, setWantMouse] = useState(false);
  const cameraPreviewRef = useRef(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [voiceUploadStatus, setVoiceUploadStatus] = useState({}); // responseKey -> uploading|uploaded|failed

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progressPercentage = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  // Get text color style with consistent opacity handling for gray colors
  const getTextColorStyle = (isSecondary = false) => {
    const color = questionnaire?.theme_text_color || questionnaire?.theme_color || '#000000';
    
    // Check if the color is gray-ish (low saturation)
    const normalizedColor = color.startsWith('#') ? color : `#${color}`;
    let hex = normalizedColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate saturation to detect gray colors
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    
    // For gray colors (low saturation), use higher opacity for secondary text
    // For colored text, use lower opacity for secondary text
    const opacity = isSecondary ? (saturation < 0.1 ? 0.7 : 0.6) : 1;
    
    return { color, opacity };
  };

  // Generate gradient background like the builder
  const generateGradientBackground = (baseColor) => {
    if (!baseColor) return '#ffffff';
    
    const normalizedColor = baseColor.startsWith('#') ? baseColor : `#${baseColor}`;
    let hex = normalizedColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Create lighter and darker variants
    const lighterColor = `rgb(${Math.min(255, r + 20)}, ${Math.min(255, g + 20)}, ${Math.min(255, b + 20)})`;
    const darkerColor = `rgb(${Math.max(0, r - 20)}, ${Math.max(0, g - 20)}, ${Math.max(0, b - 20)})`;
    
    return `linear-gradient(135deg, ${lighterColor} 0%, ${normalizedColor} 50%, ${darkerColor} 100%)`;
  };

  // Generate CSS classes for multiple choice display layout
  const getMultipleChoiceLayoutClasses = (displaySettings) => {
    const layoutType = displaySettings?.layout_type || 'grid';
    const itemsPerRow = displaySettings?.items_per_row || 3;
    
    if (layoutType === 'vertical') {
      return 'space-y-4';
    }
    
    if (layoutType === 'horizontal') {
      return 'space-x-4 flex flex-wrap';
    }
    
    // Grid layout
    const gridClasses = {
      1: 'grid grid-cols-1 gap-6',
      2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
      3: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6',
      4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
      5: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4',
      6: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4',
      7: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3',
      8: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3',
      9: 'grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2',
      10: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10 gap-2'
    };
    
    return gridClasses[itemsPerRow] || gridClasses[3];
  };

  // Generate CSS classes for individual option container
  const getOptionContainerClasses = (displaySettings) => {
    const layoutType = displaySettings?.layout_type || 'grid';
    
    if (layoutType === 'horizontal') {
      return 'space-x-3 flex items-center';
    }
    
    // Both 'grid' and 'vertical' use vertical spacing within each option
    return 'space-y-3';
  };

  // Generate CSS classes for option images based on display settings
  const getImageClasses = (displaySettings) => {
    const layoutType = displaySettings?.layout_type || 'grid';
    const itemsPerRow = displaySettings?.items_per_row || 3;
    
    const baseClasses = 'object-cover rounded-lg shadow-sm border-2 cursor-pointer';
    
    if (layoutType === 'horizontal') {
      return `w-24 h-18 ${baseClasses}`; // Smaller for horizontal layout
    }
    
    if (layoutType === 'vertical') {
      return `w-full h-48 md:h-56 ${baseClasses}`; // Full width for vertical layout
    }
    
    // Grid layout - responsive sizing based on items per row
    const sizeClasses = {
      1: 'w-full h-64 md:h-72 lg:h-80', // Large for 1 column
      2: 'w-full h-56 md:h-64 lg:h-72', // Medium-large for 2 columns
      3: 'w-full h-48 md:h-56 lg:h-64', // Medium for 3 columns
      4: 'w-full h-40 md:h-48 lg:h-56'  // Smaller for 4 columns
    };
    
    return `${sizeClasses[itemsPerRow] || sizeClasses[3]} ${baseClasses}`;
  };

  useEffect(() => {
    loadQuestionnaire();
  }, [questionnaireUuid]);

  useEffect(() => {
    const video = cameraPreviewRef.current;
    if (!video) return;
    if (recording.cameraEnabled && recording.cameraStream?.current) {
      video.srcObject = recording.cameraStream.current;
      video.play().catch(() => {});
    } else {
      video.srcObject = null;
    }
  }, [recording.cameraEnabled, recording.cameraStream, showConsent, isSaving]);

  useEffect(() => {
    // Track question view start time for timing and reset video state
    if (currentQuestion) {
      setQuestionStartTime(Date.now());
      setVideoCompleted(false); // Reset video completion for new question
    }
  }, [currentQuestion]);

  // Auto-advance timer for timed questionnaires
  useEffect(() => {
    // Clear any existing timers
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }
    if (timerInterval) {
      clearInterval(timerInterval);
    }

    // Only set up auto-advance if questionnaire is timed and we have a valid question
    if (questionnaire?.is_timed && currentQuestion && !isCompleted) {
      // Get effective durations with question-level override support
      
      // Handle question-level duration overrides properly (checking for null, undefined, and 0)
      const questionViewDuration = (currentQuestion.view_duration != null && currentQuestion.view_duration > 0) 
        ? currentQuestion.view_duration 
        : (questionnaire.default_view_duration || 5);
      const questionAnswerDuration = (currentQuestion.answer_duration != null && currentQuestion.answer_duration > 0) 
        ? currentQuestion.answer_duration 
        : (questionnaire.default_answer_duration || 30);
      
      // Check if current question has video content
      const hasVideo = (currentQuestion.media_url || currentQuestion.video_url) && 
        (currentQuestion.media_url?.includes('.mp4') || currentQuestion.media_url?.includes('.webm') || 
         currentQuestion.media_url?.includes('.mov') || currentQuestion.video_url || currentQuestion.type === 'video_only');
      
      // View duration only applies to image_only and video_only types
      const hasViewPhase = currentQuestion.type === 'image_only' || currentQuestion.type === 'video_only';
      const viewDuration = hasViewPhase ? questionViewDuration : 0;
      const answerDuration = questionAnswerDuration;
      
      const totalDuration = viewDuration + answerDuration;
      
      
      // Set initial countdown time
      setTimeRemaining(totalDuration);
      
      // Start countdown interval (update every second)
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up, but check if video needs to finish first
            if (hasVideo && !videoCompleted) {
              // Video hasn't finished, don't auto-advance yet
              return 1; // Keep timer at 1 second until video finishes
            } else {
              // No video or video completed, auto-advance
              handleNext();
              return 0;
            }
          }
          return prev - 1;
        });
      }, 1000);
      
      setTimerInterval(interval);
      
      // Set the auto-advance timer as backup (but respect video completion)
      const advanceTimer = setTimeout(() => {
        if (!hasVideo || videoCompleted) {
          handleNext();
        }
        // If video isn't completed, the interval will keep waiting
      }, totalDuration * 1000);
      
      setAutoAdvanceTimer(advanceTimer);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [currentQuestion, questionnaire, isCompleted]);

  useEffect(() => {
    if (!isSaving || !pendingNextRef.current) return;
    const work = pendingNextRef.current;
    pendingNextRef.current = null;
    work();
  }, [isSaving]);

  // Handle video completion
  const handleVideoEnded = () => {
    setVideoCompleted(true);
  };

  // Check if progression is allowed (for videos that must complete)
  const canProceedFromQuestion = () => {
    if (!currentQuestion) return false;
    
    // Check if current question has video content
    const hasVideo = (currentQuestion.media_url || currentQuestion.video_url) && 
      (currentQuestion.media_url?.includes('.mp4') || currentQuestion.media_url?.includes('.webm') || 
       currentQuestion.media_url?.includes('.mov') || currentQuestion.video_url || currentQuestion.type === 'video_only');
    
    // If has video, must be completed. If no video, can proceed normally.
    return !hasVideo || videoCompleted;
  };

  const loadQuestionnaire = async () => {
    // Reset session state so a fresh start is required each time questionnaire loads
    setResponseUuid(null);
    setIsCompleted(false);
    setCurrentQuestionIndex(0);
    setResponses({});
    setShowConsent(false);
    try {
      setLoading(true);
      const response = await getPublicQuestionnaire(questionnaireUuid);
      
      if (response && (response.questionnaire || response.title)) {
        // Handle both formats - the API returns the questionnaire data directly
        const questionnaireData = response.questionnaire || response;
        
        // Map backend question types to frontend types (same logic as builder)
        const mappedQuestions = (questionnaireData.questions || []).map(question => {
          let mappedType = question.type || 'text';
          
          // DEBUG: Log all question data to understand structure
          console.log('🔍 DEBUG: Raw question data:', {
            type: question.type,
            hasOptionsArray: Array.isArray(question.options),
            optionsLength: question.options?.length,
            optionsContent: question.options,
            questionText: question.question_text,
            hasMedia: !!(question.media_url || question.image_url || question.video_url)
          });
          
          // More conservative mapping: Only convert to image_only/video_only if there's clear evidence
          // this was created as a "no input" type, not just an image_choice without options yet
          if (question.type === 'image_choice') {
            const hasOptions = question.options?.length > 0;
            
            // Only map to image_only/video_only if:
            // 1. No options AND 
            // 2. Has media content (suggesting it's meant for viewing only) AND
            // 3. Question text suggests it's for viewing (optional additional check)
            if (!hasOptions) {
              const hasMedia = !!(question.media_url || question.image_url || question.video_url);
              const hasVideo = question.has_video || question.video_url || question.media_url?.includes('.mp4') || question.media_url?.includes('.webm') || question.media_url?.includes('.mov');
              
              // Only convert if it has media content (suggests it's meant for viewing only)
              if (hasMedia) {
                mappedType = hasVideo ? 'video_only' : 'image_only';
                
                // DEBUG: Log the type mapping
                console.log('🔄 DEBUG: Question type mapped to viewing-only:', {
                  originalType: question.type,
                  mappedType: mappedType,
                  hasOptions: hasOptions,
                  hasMedia: hasMedia,
                  hasVideo: hasVideo,
                  questionText: question.question_text
                });
              } else {
                // Keep as image_choice even without options - probably just not configured yet
                console.log('⏸️ DEBUG: Keeping as image_choice (no options, no media - likely incomplete):', {
                  type: question.type,
                  questionText: question.question_text,
                  hasOptions: hasOptions,
                  hasMedia: hasMedia
                });
              }
            } else {
              // DEBUG: Log when keeping as image_choice
              console.log('✅ DEBUG: Keeping as image_choice (has options):', {
                type: question.type,
                optionsCount: question.options?.length,
                questionText: question.question_text
              });
            }
          }
          
          return {
            ...question,
            type: mappedType
          };
        });
        
        setQuestionnaire(questionnaireData);
        setQuestions(mappedQuestions);
        setViewStartTime(Date.now());
        // Pre-select recording options based on creator's settings
        setWantCamera(!!questionnaireData.record_camera);
        setWantScreen(!!questionnaireData.record_screen);
        setWantMouse(!!questionnaireData.record_mouse);
      } else {
        setError('Questionnaire not found or not available');
      }
    } catch (err) {
      console.error('Failed to load questionnaire:', err);
      
      // Handle different error scenarios
      if (err.response?.status === 404) {
        setError(t('Questionnaires.Viewer.Error404'));
      } else if (err.response?.status === 403) {
        setError(t('Questionnaires.Viewer.Error403'));
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(t('Questionnaires.Viewer.ErrorLoad'));
      }
    } finally {
      setLoading(false);
      setShowConsent(true);
    }
  };

  const buildSubmitPayload = (question, allResponses) => {
    const questionId = question.id || question.uuid || currentQuestionIndex;
    const response = allResponses[questionId] || allResponses[questionId?.toString()] || {};
    const { value, type } = response;
    const payload = { question_uuid: question.uuid, skipped: false, answered_at: new Date().toISOString() };

    // Multi-section pages carry one answer per section; the page-level row stays empty.
    if (question.type === 'multi_section') {
      payload.section_responses = (question.sections || [])
        .map((section, index) => {
          if (section.type === 'voice_recording') return null; // uploaded on its own endpoint
          const sectionResponse = allResponses[`${questionId}-section-${section.id || index}`];
          if (!sectionResponse || sectionResponse.value === undefined || sectionResponse.value === null) return null;
          return {
            section_uuid: section.uuid,
            text_response: typeof sectionResponse.value === 'string'
              ? sectionResponse.value
              : JSON.stringify(sectionResponse.value)
          };
        })
        .filter(Boolean);
      return payload;
    }

    switch (type || question.type) {
      case 'multiple_choice':   payload.selected_option_id = value; break;
      case 'multiple_select':   payload.selected_options = Array.isArray(value) ? value : []; break;
      case 'rating':            payload.rating_value = value; break;
      case 'yes_no':            payload.boolean_response = value; break;
      // Voice answers are uploaded on their own endpoint; sending no text keeps
      // the queued transcription from being overwritten here.
      case 'voice_recording':   break;
      default:                  payload.text_response = typeof value === 'string' ? value : JSON.stringify(value || '');
    }
    return payload;
  };

  const handleStartQuestionnaire = async (screenEnabled, cameraEnabled, mouseEnabled) => {
    setIsStarting(true);
    try {
      const data = await startQuestionnaire(questionnaireUuid, {
        device_info: { userAgent: navigator.userAgent, screen: `${window.screen.width}x${window.screen.height}` },
      });
      const newResponseUuid = data.response_id || data.response_uuid || data.uuid;
      setResponseUuid(newResponseUuid);

      // Eye-tracking needs the webcam and a background to overlay onto. On desktop
      // that background is the screen recording; on mobile (no getDisplayMedia) it's
      // the per-question html2canvas screenshot — so gaze runs on both. When enabled,
      // run calibration before Q1; GazeCalibration starts the first question after.
      const gazeWanted =
        !!questionnaire?.record_gaze && screenEnabled && cameraEnabled;

      const sessionId = await recording.startSession(newResponseUuid, screenEnabled, cameraEnabled, mouseEnabled, gazeWanted);

      if (sessionId && gazeWanted) {
        setShowConsent(false);
        setShowCalibration(true);
        setIsStarting(false);
        return;
      }

      if (sessionId && questions.length > 0) {
        await recording.startQuestion(questions[0].uuid, 0);
      }
      setShowConsent(false);
    } catch (err) {
      console.error('Failed to start questionnaire session:', err);
    }
    setIsStarting(false);
  };

  // Called by GazeCalibration once setup (pass/fail/skip) completes; begins Q1.
  const handleCalibrationComplete = async () => {
    setShowCalibration(false);
    if (questions.length > 0) {
      await recording.startQuestion(questions[0].uuid, 0);
    }
  };

  const handleResponseChange = (questionId, value, type = 'text') => {
    // Use consistent ID logic
    const currentQuestionId = currentQuestion?.id || currentQuestion?.uuid || currentQuestionIndex;
    
    // Extract base question ID from section IDs (e.g., "uuid-section-id" -> "uuid")
    const baseQuestionId = questionId.toString().includes('-section-') 
      ? questionId.toString().split('-section-')[0] 
      : questionId.toString();
    
    // Only allow response changes for the current question to prevent answer leakage
    if (baseQuestionId !== currentQuestionId.toString()) {
      console.warn('Attempted to change response for non-current question:', questionId, 'current:', currentQuestionId);
      return;
    }
    
    setResponses(prev => {
      const newResponses = {
        ...prev,
        [questionId]: {
          value: value,
          type: type,
          answeredAt: Date.now(),
          timeSpent: questionStartTime ? Date.now() - questionStartTime : 0
        }
      };
      
      console.log('💾 DEBUG: Response stored:', {
        questionId,
        type,
        value,
        allResponses: Object.keys(newResponses)
      });
      
      return newResponses;
    });
  };

  // Voice answers upload as soon as recording stops; the transcription is queued
  // server-side and lands in the answer's text_response.
  const handleVoiceRecorded = async (responseKey, sectionUuid, blob, seconds) => {
    setVoiceUploadStatus(prev => ({ ...prev, [responseKey]: 'uploading' }));
    try {
      await uploadVoiceAnswer(responseUuid, currentQuestion.uuid, blob, {
        sectionUuid,
        responseDuration: seconds
      });
      setVoiceUploadStatus(prev => ({ ...prev, [responseKey]: 'uploaded' }));
      handleResponseChange(responseKey, { uploaded: true, duration: seconds }, 'voice_recording');
    } catch (err) {
      console.error('Failed to upload voice answer:', err);
      setVoiceUploadStatus(prev => ({ ...prev, [responseKey]: 'failed' }));
    }
  };

  const handleMultipleChoice = (questionId, optionId, allowMultiple = false) => {
    // Use consistent ID logic
    const currentQuestionId = currentQuestion?.id || currentQuestion?.uuid || currentQuestionIndex;
    
    // Only allow multiple choice changes for the current question
    if (questionId !== currentQuestionId) {
      console.warn('Attempted to change multiple choice for non-current question:', questionId, 'current:', currentQuestionId);
      return;
    }
    
    if (allowMultiple) {
      const currentValues = responses[questionId]?.value || [];
      const newValues = currentValues.includes(optionId) 
        ? currentValues.filter(id => id !== optionId)
        : [...currentValues, optionId];
      
      handleResponseChange(questionId, newValues, 'multiple_select');
    } else {
      handleResponseChange(questionId, optionId, 'multiple_choice');
    }
  };

  const canProceed = () => {
    // First check video completion requirement
    if (!canProceedFromQuestion()) return false;
    
    if (!currentQuestion || !currentQuestion.required) return true;
    
    // Use consistent questionId logic (same as in renderQuestion)
    const questionId = currentQuestion.id || currentQuestion.uuid || currentQuestionIndex;
    
    console.log('🔍 DEBUG: canProceed validation started:', {
      questionType: currentQuestion.type,
      questionId,
      questionRequired: currentQuestion.required
    });
    
    // For multi_section questions, don't check main question response - check sections directly
    if (currentQuestion.type === 'multi_section') {
      console.log('🔍 DEBUG: Multi-section question detected, checking sections...');
      // Skip to multi_section validation logic directly
    } else {
      const response = responses[questionId];
      if (!response) {
        console.log('❌ DEBUG: No response found for question:', questionId);
        return false;
      }
    }
    
    switch (currentQuestion.type) {
      case 'multi_section':
        // Validate all required sections within multi-section questions
        if (!currentQuestion.sections || currentQuestion.sections.length === 0) return true;
        
        return currentQuestion.sections.every(section => {
          if (!section.required) return true; // Skip validation for non-required sections
          
          // Display-only sections never need a response
          if (['text_only', 'image_only', 'video_only', 'audio_only'].includes(section.type)) return true;

          const sectionId = `${questionId}-section-${section.id || currentQuestion.sections.indexOf(section)}`;
          const sectionResponse = responses[sectionId];

          console.log('🔍 DEBUG: Section validation:', {
            sectionId,
            sectionType: section.type,
            hasResponse: !!sectionResponse,
            responseValue: sectionResponse?.value,
            isRequired: section.required
          });

          if (!sectionResponse) {
            console.log('❌ DEBUG: No response found for section:', sectionId);
            return false;
          }

          switch (section.type) {
            case 'text':
              return sectionResponse.value && sectionResponse.value.trim().length > 0;
            case 'multiple_choice':
            case 'yes_no':
            case 'rating':
              return sectionResponse.value !== undefined && sectionResponse.value !== null;
            case 'voice_recording':
              return sectionResponse.value?.uploaded === true;
            case 'table':
              // For table sections, validate that all rows have a selection
              if (!sectionResponse.value || !Array.isArray(sectionResponse.value)) {
                console.log('❌ DEBUG: Table validation failed - invalid value type:', sectionResponse.value);
                return false;
              }
              const tableData = section.table_data;
              if (!tableData || !tableData.rows) {
                console.log('⚠️ DEBUG: No table data structure found');
                return true; // No validation if no table structure
              }

              const isValid = sectionResponse.value.length === tableData.rows.length &&
                             sectionResponse.value.every(selection => selection !== null && selection !== undefined);

              console.log('🔍 DEBUG: Table validation result:', {
                responseLength: sectionResponse.value.length,
                rowsLength: tableData.rows.length,
                responseValues: sectionResponse.value,
                allRowsSelected: sectionResponse.value.every(selection => selection !== null && selection !== undefined),
                finalResult: isValid
              });

              return isValid;
            default:
              return true;
          }
        });
      case 'text':
      case 'textarea':
        const response = responses[questionId];
        if (!response) return false;
        return response.value && response.value.trim().length > 0;
      case 'multiple_choice':
        const mcResponse = responses[questionId];
        if (!mcResponse) return false;
        return mcResponse.value !== undefined && mcResponse.value !== null;
      case 'multiple_select':
        const msResponse = responses[questionId];
        if (!msResponse) return false;
        return Array.isArray(msResponse.value) && msResponse.value.length > 0;
      case 'rating':
        const ratingResponse = responses[questionId];
        if (!ratingResponse) return false;
        return ratingResponse.value !== undefined && ratingResponse.value !== null;
      case 'voice_recording':
        return responses[questionId]?.value?.uploaded === true;
      case 'image_only':
      case 'video_only':
      case 'audio_only':
      case 'text_only':
        // No input required for image-only, video-only, audio-only, and text-only questions
        return true;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (advancingRef.current) return; // ignore repeat taps during capture/save
    advancingRef.current = true;

    const snapResponseUuid = responseUuid;
    const snapQuestion = currentQuestion;
    const snapResponses = responses;
    const snapIndex = currentQuestionIndex;
    const snapQuestions = questions;

    // Snapshot the question's Areas of Interest (option/media/prompt rectangles)
    // while it's still on screen, so gaze can later be mapped to what was looked
    // at. Then capture the mobile frame before the "saving" overlay replaces it.
    recording.setQuestionAois(captureAois());
    await recording.captureQuestionFrame();

    pendingNextRef.current = async () => {
      try {
        let serverCompleted = false;

        if (snapResponseUuid && snapQuestion) {
          try {
            const result = await submitQuestionAnswer(snapResponseUuid, buildSubmitPayload(snapQuestion, snapResponses));
            if (result?.completed) serverCompleted = true;
          } catch (err) {
            console.error('Failed to submit answer:', err);
          }
          await recording.endQuestion(snapQuestion.uuid);
        }

        if (serverCompleted) {
          await recording.endSession();
          setIsSaving(false);
          setIsCompleted(true);
          return;
        }

        if (snapIndex < snapQuestions.length - 1) {
          const nextIndex = snapIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          if (recording.sessionId && snapQuestions[nextIndex]) {
            await recording.startQuestion(snapQuestions[nextIndex].uuid, nextIndex);
          }
        } else {
          handleSubmit();
        }

        setIsSaving(false);
      } finally {
        advancingRef.current = false;
      }
    };

    setIsSaving(true);
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => {
        return prev - 1;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      // The last submitQuestionAnswer call already marks the response as completed
      // server-side, so no separate completeQuestionnaire call is needed here.
      await recording.endSession();
      setIsCompleted(true);
    } catch (err) {
      console.error('Failed to submit questionnaire:', err);
      alert('Failed to submit questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render individual section input based on section type
  const renderSectionInput = (section, sectionIndex) => {
    const questionId = currentQuestion.id || currentQuestion.uuid || currentQuestionIndex;
    const sectionId = `${questionId}-section-${section.id || sectionIndex}`;
    const sectionResponse = responses[sectionId] || null;
    
    switch (section.type) {
      case 'text':
        return (
          <input
            key={`section-text-${sectionId}`}
            type="text"
            value={sectionResponse?.value || ''}
            onChange={(e) => handleResponseChange(sectionId, e.target.value, 'text')}
            placeholder={t('Questionnaires.Viewer.InputPlaceholder')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input"
            style={{ 
              color: getTextColorStyle().color,
              borderBottomColor: questionnaire?.theme_button_color || '#1e40af'
            }}
          />
        );
      
      case 'voice_recording':
        return (
          <VoiceAnswerRecorder
            key={`section-voice-${sectionId}`}
            status={voiceUploadStatus[sectionId] || null}
            accentColor={questionnaire?.theme_button_color || '#1e40af'}
            onRecorded={(blob, seconds) => handleVoiceRecorded(sectionId, section.uuid, blob, seconds)}
          />
        );

      case 'text_only':
        return (
          <div className="text-center text-gray-500 italic py-4">
            {/* This is a text-only section - no input required */}
          </div>
        );
      
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {section.options && section.options.sort((a, b) => (a.order || 0) - (b.order || 0)).map((option, optionIndex) => (
              <div key={optionIndex} data-aoi={`s${sectionIndex}-opt-${optionIndex}`} className="flex items-center space-x-3 justify-center">
                <input
                  type="radio"
                  id={`section-${sectionId}-option-${optionIndex}`}
                  name={`section-${sectionId}`}
                  value={option.text}
                  checked={sectionResponse?.value === option.text}
                  onChange={() => handleResponseChange(sectionId, option.text, 'multiple_choice')}
                  autoComplete="off"
                  className="h-4 w-4 border-0 focus:ring-0 outline-0"
                  style={{ 
                    accentColor: questionnaire?.theme_button_color || '#1e40af',
                    outline: 'none',
                    border: 'none'
                  }}
                />
                <label htmlFor={`section-${sectionId}-option-${optionIndex}`} className="text-lg cursor-pointer" 
                       style={getTextColorStyle()}>
                  {option.text}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'yes_no':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 justify-center">
              <input
                type="radio"
                id={`section-${sectionId}-yes`}
                name={`section-${sectionId}`}
                value="yes"
                checked={sectionResponse?.value === 'yes'}
                onChange={() => handleResponseChange(sectionId, 'yes', 'yes_no')}
                autoComplete="off"
                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                style={{ 
                  accentColor: questionnaire?.theme_button_color || '#1e40af',
                  outline: 'none',
                  border: 'none'
                }}
              />
              <label htmlFor={`section-${sectionId}-yes`} className="text-lg cursor-pointer" 
                     style={getTextColorStyle()}>
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-3 justify-center">
              <input
                type="radio"
                id={`section-${sectionId}-no`}
                name={`section-${sectionId}`}
                value="no"
                checked={sectionResponse?.value === 'no'}
                onChange={() => handleResponseChange(sectionId, 'no', 'yes_no')}
                autoComplete="off"
                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                style={{ 
                  accentColor: questionnaire?.theme_button_color || '#1e40af',
                  outline: 'none',
                  border: 'none'
                }}
              />
              <label htmlFor={`section-${sectionId}-no`} className="text-lg cursor-pointer" 
                     style={getTextColorStyle()}>
                No
              </label>
            </div>
          </div>
        );
      
      case 'rating':
        return (
          <div className="space-y-3">
            {section.options && section.options.sort((a, b) => (a.order || 0) - (b.order || 0)).map((option, optionIndex) => (
              <div key={optionIndex} data-aoi={`s${sectionIndex}-opt-${optionIndex}`} className="flex items-center space-x-3 justify-center">
                <input
                  type="radio"
                  id={`section-${sectionId}-rating-${optionIndex}`}
                  name={`section-${sectionId}`}
                  value={option.text}
                  checked={sectionResponse?.value === option.text}
                  onChange={() => handleResponseChange(sectionId, option.text, 'rating')}
                  autoComplete="off"
                  className="h-4 w-4 border-0 focus:ring-0 outline-0"
                  style={{ 
                    accentColor: questionnaire?.theme_button_color || '#1e40af',
                    outline: 'none',
                    border: 'none'
                  }}
                />
                <label htmlFor={`section-${sectionId}-rating-${optionIndex}`} className="text-lg cursor-pointer" 
                       style={getTextColorStyle()}>
                  {option.text}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'image_only':
        return (
          <div data-aoi={`s${sectionIndex}-media`} className="text-center py-8">
            {section.media_url ? (
              <img
                src={section.media_url}
                alt="Section content"
                className="max-w-full h-auto mx-auto rounded-lg"
                style={{ maxHeight: '400px' }}
              />
            ) : (
              <PhotoIcon className="mx-auto h-16 w-16 text-gray-400" />
            )}
          </div>
        );
      
      case 'video_only':
        return (
          <div data-aoi={`s${sectionIndex}-media`} className="text-center py-8">
            {section.media_url ? (
              <video
                src={section.media_url}
                controls
                className="max-w-full h-auto mx-auto rounded-lg"
                style={{ maxHeight: '400px' }}
              />
            ) : (
              <VideoCameraIcon className="mx-auto h-16 w-16 text-gray-400" />
            )}
          </div>
        );

      case 'audio_only':
        return (
          <div data-aoi={`s${sectionIndex}-media`} className="text-center py-8">
            {section.media_url ? (
              <audio
                src={section.media_url}
                controls
                autoPlay
                className="w-full mx-auto"
              >
                {t('Questionnaires.Viewer.NoAudioSupport')}
              </audio>
            ) : (
              <SpeakerWaveIcon className="mx-auto h-16 w-16 text-gray-400" />
            )}
          </div>
        );

      case 'table':
        if (!section.table_data || !section.table_data.rows || !section.table_data.columns) {
          return (
            <div className="text-center text-gray-500 py-4">
              {t('Questionnaires.Viewer.TableNotConfigured')}
            </div>
          );
        }

        const { rows, columns, header_mode } = section.table_data;

        return (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 min-w-max">
              {/* Show header row only when header_mode is 'headers' */}
              {header_mode !== 'cells' && (
                <thead>
                  <tr>
                    <th className="border border-gray-300 p-3 bg-gray-50 font-medium text-left" style={getTextColorStyle()}>
                      {/* Empty corner cell */}
                    </th>
                    {columns.map((column) => (
                      <th key={column.id} className="border border-gray-300 p-3 bg-gray-50 font-medium text-center" style={getTextColorStyle()}>
                        {column.text}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={row.id}>
                    <td className="border border-gray-300 p-3 bg-gray-50 font-medium" style={getTextColorStyle()}>
                      {row.text}
                    </td>
                    {columns.map((column, colIndex) => (
                      <td key={`${row.id}-${column.id}`} className="border border-gray-300 p-3 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <input
                            type="radio"
                            id={`table-${sectionId}-row-${rowIndex}-col-${colIndex}`}
                            name={`table-${sectionId}-row-${rowIndex}`}
                            value={column.id}
                            checked={sectionResponse?.value?.[rowIndex] === column.id}
                            onChange={() => {
                              // Handle table response - store column selection for each row
                              const newTableResponse = [...(sectionResponse?.value || new Array(rows.length).fill(null))];
                              newTableResponse[rowIndex] = column.id;
                              handleResponseChange(sectionId, newTableResponse, 'table');
                            }}
                            autoComplete="off"
                            className="h-4 w-4 focus:ring-2 focus:ring-opacity-50"
                            style={{ 
                              accentColor: questionnaire?.theme_button_color || '#1e40af',
                              borderColor: '#d1d5db',
                              borderWidth: '1px',
                              focusRingColor: questionnaire?.theme_button_color || '#1e40af'
                            }}
                          />
                          {/* Show column header text in cell when header_mode is 'cells' */}
                          {header_mode === 'cells' && (
                            <label htmlFor={`table-${sectionId}-row-${rowIndex}-col-${colIndex}`} className="text-sm cursor-pointer" 
                                   style={getTextColorStyle()}>
                              {column.text}
                            </label>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      
      default:
        return (
          <div className="text-center text-gray-500 py-4">
            {t('Questionnaires.Viewer.UnknownSectionType', { type: section.type })}
          </div>
        );
    }
  };

  const renderQuestion = (question) => {
    // Use question ID or fall back to question index if ID is missing
    const questionId = question.id || question.uuid || currentQuestionIndex;
    
    // Get the saved response for this specific question
    const response = responses[questionId] || null;
    
    // DEBUG: Log question type to see what's being detected
    console.log('🔍 DEBUG: Question type detected:', {
      questionType: question.type,
      questionId: questionId,
      questionText: question.question_text,
      hasMedia: !!(question.media_url || question.image_url || question.video_url),
      mediaUrl: question.media_url,
      imageUrl: question.image_url,
      videoUrl: question.video_url
    });
    
    switch (question.type) {
      case 'text':
        const textValue = response?.value || '';
        
        return (
          <>
            <input
              key={`text-${questionId}-${currentQuestionIndex}`}
              type="text"
              value={textValue}
              onChange={(e) => handleResponseChange(questionId, e.target.value, 'text')}
              placeholder={t('Questionnaires.Viewer.InputPlaceholder')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input"
              style={{ 
                color: getTextColorStyle().color,
                borderBottomColor: questionnaire?.theme_button_color || '#1e40af'
              }}
            />
            
            {/* Helper Text for Text Questions */}
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              {t('Questionnaires.Viewer.InputHelperText')}
            </p>
          </>
        );

      case 'text_only':
        return (
          <div className="text-center text-gray-500 italic py-8">
            {/* This is a text-only page - no input required */}
          </div>
        );

      case 'textarea':
        return (
          <>
            <textarea
              key={`textarea-${questionId}-${currentQuestionIndex}`}
              value={response?.value || ''}
              onChange={(e) => handleResponseChange(questionId, e.target.value, 'textarea')}
              rows={4}
              placeholder={t('Questionnaires.Viewer.InputPlaceholder')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 resize-none questionnaire-input"
              style={{ 
                color: getTextColorStyle().color,
                borderBottomColor: questionnaire?.theme_button_color || '#1e40af'
              }}
            />
            
            {/* Helper Text for Textarea Questions */}
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              {t('Questionnaires.Viewer.InputHelperText')}
            </p>
          </>
        );

      case 'yes_no':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 justify-center">
              <input
                key={`yes-${questionId}-${currentQuestionIndex}`}
                type="radio"
                id={`yes-option-${currentQuestionIndex}`}
                name={`question-${question.id}-${currentQuestionIndex}`}
                value="yes"
                checked={response?.value === 'yes'}
                onChange={() => handleResponseChange(questionId, 'yes', 'yes_no')}
                autoComplete="off"
                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                style={{ 
                  accentColor: questionnaire?.theme_button_color || '#1e40af',
                  outline: 'none',
                  border: 'none'
                }}
              />
              <label htmlFor={`yes-option-${currentQuestionIndex}`} className="text-lg cursor-pointer" 
                     style={getTextColorStyle()}>
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-3 justify-center">
              <input
                key={`no-${questionId}-${currentQuestionIndex}`}
                type="radio"
                id={`no-option-${currentQuestionIndex}`}
                name={`question-${question.id}-${currentQuestionIndex}`}
                value="no"
                checked={response?.value === 'no'}
                onChange={() => handleResponseChange(questionId, 'no', 'yes_no')}
                autoComplete="off"
                className="h-4 w-4 border-0 focus:ring-0 outline-0"
                style={{ 
                  accentColor: questionnaire?.theme_button_color || '#1e40af',
                  outline: 'none',
                  border: 'none'
                }}
              />
              <label htmlFor={`no-option-${currentQuestionIndex}`} className="text-lg cursor-pointer"
                     style={getTextColorStyle()}>
                No
              </label>
            </div>
          </div>
        );

      case 'multiple_choice':
        return (
          <div className={getMultipleChoiceLayoutClasses(question.display_settings)}>
            {question.options?.map((option) => (
              <div key={option.id} className={getOptionContainerClasses(question.display_settings)}>
                {/* Radio button and text - alignment based on display settings */}
                <div className="flex items-center space-x-3">
                  <input
                    key={`option-${option.id}-${currentQuestionIndex}`}
                    type="radio"
                    id={`option-${option.id}-${currentQuestionIndex}`}
                    name={`question-${question.id}-${currentQuestionIndex}`}
                    value={option.id}
                    checked={response?.value === option.id}
                    onChange={() => handleMultipleChoice(questionId, option.id, false)}
                    autoComplete="off"
                    className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                    style={{ 
                      accentColor: questionnaire?.theme_button_color || '#1e40af',
                      outline: 'none',
                      border: 'none'
                    }}
                  />
                  <label 
                    htmlFor={`option-${option.id}-${currentQuestionIndex}`} 
                    className="text-lg cursor-pointer flex-grow"
                    style={getTextColorStyle()}
                  >
                    {option.option_text}
                  </label>
                </div>
                {/* Show option image below text if available - full width */}
                {(option.image_url || option.media_url || option.video_url) && (
                  <div className="w-full">
                    {/* Check if option has video */}
                    {(option.media_url || option.video_url) && 
                    (option.media_url?.includes('.mp4') || option.media_url?.includes('.webm') || 
                     option.media_url?.includes('.mov') || option.video_url) ? (
                      <video 
                        controls
                        className={getImageClasses(question.display_settings)}
                        style={{ borderColor: questionnaire?.theme_button_color || '#1e40af' }}
                        onError={(e) => {
                          console.error('Failed to load option video:', option.media_url || option.video_url)
                        }}
                        onEnded={handleVideoEnded}
                      >
                        <source src={option.media_url || option.video_url} type="video/mp4" />
                        {t('Questionnaires.Viewer.NoVideoSupport')}
                      </video>
                    ) : (
                      <img 
                        src={option.image_url || option.media_url}
                        alt={`Option ${option.option_text || 'image'}`}
                        className={getImageClasses(question.display_settings)}
                        style={{ borderColor: questionnaire?.theme_button_color || '#1e40af' }}
                        onClick={() => handleMultipleChoice(questionId, option.id, false)}
                        onError={(e) => {
                          console.error('Failed to load option image:', option.image_url || option.media_url)
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'rating':
        const ratingOptions = question.options?.length > 0 ? question.options : [
          { id: 1, option_text: 'Excellent' },
          { id: 2, option_text: 'Good' },
          { id: 3, option_text: 'Average' },
          { id: 4, option_text: 'Poor' },
          { id: 5, option_text: 'Very Poor' }
        ];
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {ratingOptions.map((option) => (
              <div key={option.id} className="space-y-3">
                {/* Radio button and text - left aligned */}
                <div className="flex items-center space-x-3">
                  <input
                    key={`rating-${option.id}-${currentQuestionIndex}`}
                    type="radio"
                    id={`rating-${option.id}-${currentQuestionIndex}`}
                    name={`rating-${question.id}-${currentQuestionIndex}`}
                    value={option.id}
                    checked={response?.value === option.id}
                    onChange={() => handleResponseChange(questionId, option.id, 'rating')}
                    autoComplete="off"
                    className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                    style={{ 
                      accentColor: questionnaire?.theme_button_color || '#1e40af',
                      outline: 'none',
                      border: 'none'
                    }}
                  />
                  <label 
                    htmlFor={`rating-${option.id}-${currentQuestionIndex}`} 
                    className="text-lg cursor-pointer flex-grow"
                    style={getTextColorStyle()}
                  >
                    {option.option_text}
                  </label>
                </div>
                {/* Show rating option image below text if available - full width */}
                {(option.image_url || option.media_url || option.video_url) && (
                  <div className="w-full">
                    {/* Check if rating option has video */}
                    {(option.media_url || option.video_url) && 
                    (option.media_url?.includes('.mp4') || option.media_url?.includes('.webm') || 
                     option.media_url?.includes('.mov') || option.video_url) ? (
                      <video 
                        controls
                        className="w-full h-48 md:h-56 lg:h-64 object-cover rounded-lg shadow-sm border-2"
                        style={{ borderColor: questionnaire?.theme_button_color || '#1e40af' }}
                        onError={(e) => {
                          console.error('Failed to load rating option video:', option.media_url || option.video_url)
                        }}
                        onEnded={handleVideoEnded}
                      >
                        <source src={option.media_url || option.video_url} type="video/mp4" />
                        {t('Questionnaires.Viewer.NoVideoSupport')}
                      </video>
                    ) : (
                      <img 
                        src={option.image_url || option.media_url}
                        alt={`Rating ${option.option_text || 'image'}`}
                        className="w-full h-48 md:h-56 lg:h-64 object-cover rounded-lg shadow-sm border-2 cursor-pointer"
                        style={{ borderColor: questionnaire?.theme_button_color || '#1e40af' }}
                        onClick={() => handleResponseChange(questionId, option.id, 'rating')}
                        onError={(e) => {
                          console.error('Failed to load rating option image:', option.image_url || option.media_url)
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'image_only':
      case 'video_only':
      case 'audio_only':
        /* No input fields for image-only, video-only, and audio-only question types */
        return null;

      case 'voice_recording':
        return (
          <VoiceAnswerRecorder
            key={`question-voice-${questionId}`}
            status={voiceUploadStatus[questionId] || null}
            accentColor={questionnaire?.theme_button_color || '#1e40af'}
            onRecorded={(blob, seconds) => handleVoiceRecorded(questionId, null, blob, seconds)}
          />
        );

      case 'multi_section':
        /* Multi-section questions with ordered sections */
        if (!question.sections || question.sections.length === 0) {
          return (
            <div className="text-center text-gray-500 py-8">
              No sections found for this page.
            </div>
          );
        }

        // Sort sections by order field to ensure proper display order
        const sortedSections = [...question.sections].sort((a, b) => (a.order || 0) - (b.order || 0));

        return (
          <div className="space-y-8">
            {sortedSections.map((section, index) => (
              <div key={section.id || index} className="section-container">
                {/* Section Title */}
                {section.question_text && (
                  <h3 data-aoi={`s${index}-title`} className="text-lg font-medium mb-4" style={getTextColorStyle()}>
                    {section.question_text}
                  </h3>
                )}
                
                {/* Section Description */}
                {section.description && (
                  <p className="text-sm mb-4" style={getTextColorStyle(true)}>
                    {section.description}
                  </p>
                )}
                
                {/* Render section content based on section type */}
                {renderSectionInput(section, index)}
              </div>
            ))}
          </div>
        );

      default:
        /* Default Text Input for Unknown Types */
        return (
          <>
            <input
              key={`default-${questionId}-${currentQuestionIndex}`}
              type="text"
              value={response?.value || ''}
              onChange={(e) => handleResponseChange(questionId, e.target.value, 'text')}
              placeholder={t('Questionnaires.Viewer.InputPlaceholder')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input"
              style={{ 
                color: getTextColorStyle().color,
                borderBottomColor: questionnaire?.theme_button_color || '#1e40af'
              }}
            />
            
            {/* Helper Text for Text Questions */}
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              {t('Questionnaires.Viewer.InputHelperText')}
            </p>
          </>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <UpgradedLoader className="animate-spin mx-auto mb-4" w={8} h={8} bg="bg-blue-500" />
          <p className="text-gray-600">{t('Questionnaires.Viewer.Loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('Questionnaires.Viewer.Unavailable')}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                loadQuestionnaire();
              }}
              className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              {t('Questionnaires.Viewer.ButtonTryAgain')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              {t('Questionnaires.Viewer.ButtonGoHome')}
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t('Questionnaires.Viewer.CommonIssuesTitle')}</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {t('Questionnaires.Viewer.CommonIssueDraft')}</li>
              <li>• {t('Questionnaires.Viewer.CommonIssueLink')}</li>
              <li>• {t('Questionnaires.Viewer.CommonIssueDeactivated')}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (showCalibration) {
    return <GazeCalibration recording={recording} onComplete={handleCalibrationComplete} />;
  }

  if (showConsent && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
          {questionnaire?.title && (
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{questionnaire.title}</h1>
          )}
          {questionnaire?.description && (
            <p className="text-gray-600 text-sm mb-6">{questionnaire.description}</p>
          )}
          {(questionnaire?.record_camera || questionnaire?.record_screen || questionnaire?.record_mouse || questionnaire?.record_gaze) && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 leading-relaxed">
                {t('Questionnaires.Viewer.ConsentNote')}
              </p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            {questionnaire?.record_camera && (
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={wantCamera}
                  onChange={(e) => setWantCamera(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <UserIcon className="w-5 h-5 text-gray-500 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Viewer.ConsentCameraTitle')}</div>
                  <div className="text-xs text-gray-500">{t('Questionnaires.Viewer.ConsentCameraDesc')}</div>
                </div>
              </label>
            )}

            {questionnaire?.record_screen && recording.canScreenRecord && (
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={wantScreen}
                  onChange={(e) => setWantScreen(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <ComputerDesktopIcon className="w-5 h-5 text-gray-500 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Viewer.ConsentScreenTitle')}</div>
                  <div className="text-xs text-gray-500">{t('Questionnaires.Viewer.ConsentScreenDesc')}</div>
                </div>
              </label>
            )}

            {questionnaire?.record_screen && !recording.canScreenRecord && (
              <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50">
                <ComputerDesktopIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-800">{t('Questionnaires.Viewer.ConsentScreenUnavailableTitle')}</div>
                  <div className="text-xs text-amber-600">{t('Questionnaires.Viewer.ConsentScreenUnavailableDesc')}</div>
                </div>
              </div>
            )}

            {questionnaire?.record_mouse && (
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={wantMouse}
                  onChange={(e) => setWantMouse(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <ComputerDesktopIcon className="w-5 h-5 text-gray-500 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('Questionnaires.Viewer.ConsentMouseTitle')}</div>
                  <div className="text-xs text-gray-500">{t('Questionnaires.Viewer.ConsentMouseDesc')}</div>
                </div>
              </label>
            )}

            {questionnaire?.record_gaze && recording.canScreenRecord && (
              <div className="flex items-start gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50">
                <EyeIcon className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-blue-800">{t('Questionnaires.Viewer.ConsentGazeTitle')}</div>
                  <div className="text-xs text-blue-600">{t('Questionnaires.Viewer.ConsentGazeDesc')}</div>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => handleStartQuestionnaire(wantScreen, wantCamera, wantMouse)}
            disabled={isStarting}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-blue-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {isStarting && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            {t('Questionnaires.Viewer.ButtonStart')}
          </button>

          {(recording.permissionDenied || recording.cameraPermissionDenied) && (
            <p className="mt-3 text-xs text-amber-600 text-center">
              {t('Questionnaires.Viewer.PermissionDenied')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (isSaving) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: generateGradientBackground(questionnaire?.theme_background_color || '#ffffff') }}
      >
        <div className="flex flex-col items-center gap-4 bg-white/90 rounded-2xl px-10 py-8 shadow-lg">
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200"
            style={{ borderTopColor: questionnaire?.theme_button_color || '#1e40af' }}
          />
          <p className="text-gray-700 text-lg font-medium">{t('Questionnaires.Viewer.SavingResponse')}</p>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6 bg-white rounded-lg shadow-lg">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('Questionnaires.Viewer.ThankYou')}</h2>
          <p className="text-gray-600 mb-4">
            {questionnaire?.thank_you_message || t('Questionnaires.Viewer.SuccessMessage')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {t('Questionnaires.Viewer.ButtonDone')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-8 overflow-y-auto flex flex-col relative"
      style={{
        background: generateGradientBackground(questionnaire?.theme_background_color || '#ffffff'),
        transition: 'background 0.3s ease'
      }}
    >
      {/* Recording badge */}
      {(recording.screenEnabled || recording.cameraEnabled) && (
        <div className="fixed top-4 left-4 z-10 flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1 shadow-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
          <span className="text-xs font-medium text-gray-600">{t('Questionnaires.Viewer.RecordingBadge')}</span>
        </div>
      )}

      {/* Camera PiP preview — always rendered so ref is valid when stream arrives */}
      <div className={`fixed top-4 right-4 z-50 w-32 h-24 rounded-xl overflow-hidden shadow-lg border border-white/30 ${recording.cameraEnabled ? '' : 'hidden'}`}>
        <video
          ref={cameraPreviewRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover bg-black"
          style={{ transform: 'scaleX(-1)' }}
        />
      </div>

      {/* Countdown Circle for Timed Questionnaires */}
      {questionnaire?.is_timed && !isCompleted && timeRemaining > 0 && (
        <div className="fixed top-4 right-4 z-10">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg"
            style={{
              backgroundColor: questionnaire?.theme_button_color || '#1e40af'
            }}
          >
            {timeRemaining}
          </div>
        </div>
      )}
      
      <div className="w-full max-w-4xl mx-auto h-full flex flex-col justify-center flex-1">
        {currentQuestion && (
          <>
            <div className="flex-1 flex flex-col justify-center">
              {(currentQuestion.type === 'image_only' || currentQuestion.type === 'video_only' || currentQuestion.type === 'image_then_question') ? (
                /* Media-Only Page Layout */
                <div className="space-y-6 text-center">
                  {/* Page Title/Caption */}
                  <div>
                    <h2 data-aoi="prompt" className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 leading-tight text-center p-0 m-0"
                        style={getTextColorStyle()}
                    >
                      {currentQuestion.question_text}
                      {currentQuestion.required && <span className="text-red-500 ml-1">*</span>}
                    </h2>
                  </div>
                  
                  {/* Optional Description */}
                  {currentQuestion.description && (
                    <div>
                      <p className="w-full text-base italic bg-transparent border-0 focus:outline-none focus:ring-0 text-center p-0 m-0"
                         style={getTextColorStyle()}
                      >
                        {currentQuestion.description}
                      </p>
                    </div>
                  )}

                  {/* Media Display Area */}
                  <div className="rounded-lg p-4 text-center transition-colors">
                    {/* Enhanced media detection - support both images and videos for all media types */}
                    {(currentQuestion.media_url || currentQuestion.image_url || currentQuestion.video_url) ? (
                      /* Check if it's a video file */
                      (currentQuestion.media_url || currentQuestion.video_url) && 
                      (currentQuestion.media_url?.includes('.mp4') || currentQuestion.media_url?.includes('.webm') || 
                       currentQuestion.media_url?.includes('.mov') || currentQuestion.video_url || currentQuestion.type === 'video_only') ? (
                        <video 
                          controls
                          className="w-full max-h-[70vh] mx-auto rounded-lg shadow-lg"
                          onError={(e) => {
                            console.error('Failed to load video:', currentQuestion.media_url || currentQuestion.video_url)
                          }}
                          onEnded={handleVideoEnded}
                        >
                          <source src={currentQuestion.media_url || currentQuestion.video_url} type="video/mp4" />
                          {t('Questionnaires.Viewer.NoVideoSupport')}
                        </video>
                      ) : (
                        /* It's an image */
                        <img 
                          src={currentQuestion.media_url || currentQuestion.image_url}
                          alt="Question visual"
                          className="w-full max-h-[70vh] object-contain mx-auto rounded-lg shadow-lg"
                          onError={(e) => {
                            console.error('Failed to load image:', currentQuestion.media_url || currentQuestion.image_url)
                            e.target.style.display = 'none'
                          }}
                        />
                      )
                    ) : (
                      <div className="text-gray-500 py-12">
                        <PhotoIcon className="h-16 w-16 mx-auto mb-4" />
                        <p>{t('Questionnaires.Viewer.NoMediaAvailable')}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Regular Question Layout */
                <div className="space-y-6 text-left">
                  {/* Question Text */}
                  <div>
                    <h2 className="w-full text-2xl md:text-3xl font-light bg-transparent border-0 focus:outline-none focus:ring-0 leading-tight text-left p-0 m-0"
                        style={getTextColorStyle()}
                    >
                      {currentQuestion.question_text}
                      {currentQuestion.required && <span className="text-red-500 ml-1">*</span>}
                    </h2>
                  </div>
                  
                  {/* Optional Description */}
                  {currentQuestion.description && (
                    <div>
                      <p className="w-full text-base italic bg-transparent border-0 focus:outline-none focus:ring-0 text-left p-0 m-0"
                         style={getTextColorStyle()}
                      >
                        {currentQuestion.description}
                      </p>
                    </div>
                  )}
                  
                  {/* Media Area for image_then_question, image_choice, and video_only types */}
                  {(currentQuestion.media_url || currentQuestion.image_url || currentQuestion.video_url) && 
                   (currentQuestion.type === 'image_then_question' || currentQuestion.type === 'image_choice' || currentQuestion.type === 'video_only') && (
                    <div className="rounded-lg p-4 text-center">
                      {/* Check if it's a video first */}
                      {(currentQuestion.media_url || currentQuestion.video_url) && 
                       (currentQuestion.media_url?.includes('.mp4') || currentQuestion.media_url?.includes('.webm') || 
                        currentQuestion.media_url?.includes('.mov') || currentQuestion.video_url || currentQuestion.type === 'video_only') ? (
                        <video 
                          controls
                          className="w-full max-h-[50vh] mx-auto rounded-lg shadow-lg"
                          onError={(e) => {
                            console.error('Failed to load video:', currentQuestion.media_url || currentQuestion.video_url)
                          }}
                          onEnded={handleVideoEnded}
                        >
                          <source src={currentQuestion.media_url || currentQuestion.video_url} type="video/mp4" />
                          {t('Questionnaires.Viewer.NoVideoSupport')}
                        </video>
                      ) : (
                        <img 
                          src={currentQuestion.media_url || currentQuestion.image_url}
                          alt="Question visual"
                          className="w-full max-h-[50vh] object-contain mx-auto rounded-lg shadow-lg"
                          onError={(e) => {
                            console.error('Failed to load image:', currentQuestion.media_url || currentQuestion.image_url)
                            e.target.style.display = 'none'
                          }}
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Answer Area */}
                  <div>
                    {renderQuestion(currentQuestion)}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation - Hidden for timed questionnaires */}
            {!questionnaire?.is_timed && (
              <div className="flex justify-between items-center pt-6 mt-6">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className={`flex items-center space-x-2 px-6 py-3 transition-colors ${
                    currentQuestionIndex === 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>{t('Questionnaires.Viewer.ButtonPrevious')}</span>
                </button>

                <div className="text-center">
                  {questionnaire?.show_progress_bar && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{t('Questionnaires.Viewer.Progress', { current: currentQuestionIndex + 1, total: totalQuestions })}</span>
                      <div className="w-24 h-1 bg-gray-200 rounded-full">
                        <div 
                          className="h-1 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${progressPercentage}%`,
                            backgroundColor: questionnaire?.theme_button_color || '#1e40af'
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleNext}
                  disabled={!canProceed() || isSubmitting || isSaving}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors font-medium text-white`}
                  style={{
                    backgroundColor: (!canProceed() || isSubmitting || isSaving)
                      ? '#D1D5DB'
                      : (questionnaire?.theme_button_color || '#1e40af')
                  }}
                >
                  <span>
                    {currentQuestionIndex === totalQuestions - 1 ? t('Questionnaires.Viewer.ButtonSubmit') : t('Questionnaires.Viewer.ButtonNext')}
                  </span>
                  {currentQuestionIndex < totalQuestions - 1 && (
                    <ArrowRightIcon className="w-4 h-4" />
                  )}
                  {isSubmitting && (
                    <UpgradedLoader className="animate-spin" w={4} h={4} bg="bg-white" />
                  )}
                </button>
              </div>
            )}
            
            {/* Progress bar for timed questionnaires (without navigation buttons) */}
            {questionnaire?.is_timed && questionnaire?.show_progress_bar && (
              <div className="flex justify-center pt-6 mt-6">
                <div className="text-center">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>{t('Questionnaires.Viewer.Progress', { current: currentQuestionIndex + 1, total: totalQuestions })}</span>
                    <div className="w-24 h-1 bg-gray-200 rounded-full">
                      <div 
                        className="h-1 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${progressPercentage}%`,
                          backgroundColor: questionnaire?.theme_button_color || '#1e40af'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireViewer;